/**
 * OnlineJournal.tsx — Журнал сроковых препаратов (роль: Пользователь / Аптека)
 *
 * Оркестрирует хуки и компоненты. Не содержит бизнес-логики напрямую.
 */

import React, { useEffect, useCallback, useState } from 'react';
import { Box, Alert } from '@mui/material';
import axios from 'axios';

import {
  useJournalFilters,
  useJournalEntries,
  useJournalKpi,
  useFilterOptions,
  useColumnVisibility,
} from './hooks';

import {
  KpiBar,
  JournalToolbar,
  JournalTable,
  DeleteDialog,
  JournalEntryDialog,
} from './components';

import { JournalEntry } from './types';
import { API_BASE } from './constants';
import { getMonthsList } from './utils';

const MONTHS_LIST = getMonthsList();

const OnlineJournal: React.FC = () => {
  // ── Pharmacy identification ──
  const pharmacyNum = localStorage.getItem('pharmacyNum') || '';
  const pharmacyGuid = localStorage.getItem('guid') || '';
  const username = localStorage.getItem('username') || '';

  // ── Filters, sort, pagination, column visibility ──
  const filters = useJournalFilters();
  const columnVisibility = useColumnVisibility();

  // ── Data hooks ──
  const {
    entries, tableLoading, pagination, periodStatus, fetchEntries,
  } = useJournalEntries(pharmacyGuid, filters.filterParams, filters.orderBy, filters.order);

  const { kpi, kpiLoading, fetchKpi } = useJournalKpi(pharmacyGuid, filters.filterParams);
  const { filterOptions, fetchFilterOptions } = useFilterOptions(pharmacyGuid);

  // ── Dialog state ──
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<JournalEntry | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState<JournalEntry | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Initial load ──
  useEffect(() => {
    if (!pharmacyGuid) return;
    fetchEntries(filters.page);
    fetchFilterOptions();
  }, [pharmacyGuid]);

  // ── Reload on filter/sort/page change ──
  useEffect(() => {
    if (!pharmacyGuid) return;
    fetchEntries(filters.page);
    fetchKpi();
  }, [filters.filterParams, filters.orderBy, filters.order, filters.page]);

  // ── Handlers ──
  const handleRefresh = useCallback(() => {
    fetchEntries(filters.page);
    fetchKpi();
    fetchFilterOptions();
  }, [filters.page]);

  const handlePageChange = useCallback((newPage: number) => {
    filters.setPage(newPage);
  }, [filters]);

  const handleEdit = (entry: JournalEntry) => {
    setEditEntry(entry);
    setEntryDialogOpen(true);
  };

  const handleAdd = () => {
    setEditEntry(null);
    setEntryDialogOpen(true);
  };

  const handleSaveEntry = async (entry: JournalEntry) => {
    try {
      const payload = {
        ...entry,
        createdBy: username,
      };
      if (entry.id) {
        await axios.put(`${API_BASE}/journal-user/${entry.id}`, payload);
      } else {
        await axios.post(`${API_BASE}/journal-user/upsert`, payload);
      }
      fetchEntries(filters.page);
      fetchKpi();
      return true;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleDeleteClick = (entry: JournalEntry) => {
    setDeleteEntry(entry);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteEntry?.id) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await axios.delete(`${API_BASE}/journal-user/${deleteEntry.id}`, {
        data: { deletedBy: username },
      });
      setDeleteDialogOpen(false);
      setDeleteEntry(null);
      fetchEntries(filters.page);
      fetchKpi();
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Ошибка при удалении');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Guard: no pharmacy ──
  if (!pharmacyGuid) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Не удалось определить аптеку. Пожалуйста, войдите в систему заново.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── KPI Bar ── */}
      <KpiBar kpi={kpi} kpiLoading={kpiLoading} periodStatus={periodStatus} />

      {/* ── Toolbar ── */}
      <JournalToolbar
        pharmacyGuid={pharmacyGuid}
        searchTerm={filters.searchTerm}
        setSearchTerm={filters.setSearchTerm}
        selectedMonth={filters.selectedMonth}
        setSelectedMonth={(v) => { filters.setSelectedMonth(v); filters.setPage(1); }}
        selectedCategory={filters.selectedCategory}
        setSelectedCategory={(v) => { filters.setSelectedCategory(v); filters.setPage(1); }}
        selectedManager={filters.selectedManager}
        setSelectedManager={(v) => { filters.setSelectedManager(v); filters.setPage(1); }}
        expiryFrom={filters.expiryFrom}
        setExpiryFrom={filters.setExpiryFrom}
        expiryTo={filters.expiryTo}
        setExpiryTo={filters.setExpiryTo}
        filterOptions={filterOptions}
        monthsList={MONTHS_LIST}
        filterParams={filters.filterParams}
        orderBy={filters.orderBy}
        order={filters.order}
        kpi={kpi}
        periodStatus={periodStatus}
        columnVisibility={columnVisibility}
        onReset={() => { filters.handleReset(); }}
        onRefresh={handleRefresh}
        onAdd={handleAdd}
      />

      {/* ── Table ── */}
      <JournalTable
        entries={entries}
        tableLoading={tableLoading}
        pagination={pagination}
        kpi={kpi}
        page={filters.page}
        orderBy={filters.orderBy}
        order={filters.order}
        onSort={filters.handleSort}
        onPageChange={handlePageChange}
        filterParams={filters.filterParams}
        periodStatus={periodStatus}
        gridTemplate={columnVisibility.gridTemplate}
        visibleDefs={columnVisibility.visibleDefs}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
      />

      {/* ── Journal Entry Dialog ── */}
      <JournalEntryDialog
        open={entryDialogOpen}
        onClose={() => { setEntryDialogOpen(false); setEditEntry(null); }}
        onSave={handleSaveEntry}
        editEntry={editEntry}
        pharmacyNum={pharmacyNum}
        pharmacyGuid={pharmacyGuid}
      />

      {/* ── Delete Dialog ── */}
      <DeleteDialog
        open={deleteDialogOpen}
        entry={deleteEntry}
        loading={deleteLoading}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Delete error */}
      {deleteError && !deleteDialogOpen && (
        <Alert severity="error" onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
    </Box>
  );
};

export default OnlineJournal;
